import { useState } from 'react'
import { useAuthStore } from '../../store/authStore'
import { useChangePassword } from '../../api/auth'
import { useToast } from '../../hooks/useToast'

export default function AccountTab() {
  const user = useAuthStore((s) => s.user)
  const authMode = useAuthStore((s) => s.authMode)
  const { mutate: changePassword, isPending } = useChangePassword()
  const { toast } = useToast()

  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')

  function handlePasswordChange(e: React.FormEvent) {
    e.preventDefault()
    if (next !== confirm) {
      toast({ title: 'Passwords do not match', variant: 'destructive' })
      return
    }
    if (next.length < 8) {
      toast({ title: 'Password must be at least 8 characters', variant: 'destructive' })
      return
    }
    changePassword(
      { currentPassword: current, newPassword: next },
      {
        onSuccess: () => {
          toast({ title: 'Password changed', description: 'Please log in again.' })
          setCurrent('')
          setNext('')
          setConfirm('')
        },
        onError: () => toast({ title: 'Incorrect current password', variant: 'destructive' }),
      }
    )
  }

  return (
    <div className="space-y-8">
      {/* Profile */}
      <div className="space-y-4">
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Profile</h2>
        <div className="rounded-lg border border-border p-4 space-y-3">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-primary/20 flex items-center justify-center font-mono font-bold text-primary">
              {user?.username?.[0]?.toUpperCase() ?? '?'}
            </div>
            <div>
              <div className="font-medium">{user?.displayName ?? user?.username}</div>
              <div className="text-xs text-muted-foreground font-mono">{user?.username}</div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <span className="text-muted-foreground">Role:</span>
            <span className="font-mono capitalize">{user?.role}</span>
            <span className="text-muted-foreground">Auth Mode:</span>
            <span className="font-mono">{authMode ?? 'unknown'}</span>
          </div>
        </div>
      </div>

      {/* Password change (local mode only) */}
      {authMode === 'local' ? (
        <div className="space-y-4">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Change Password</h2>
          <form onSubmit={handlePasswordChange} className="space-y-4">
            {([
              { id: 'current', label: 'Current Password', value: current, onChange: setCurrent },
              { id: 'new', label: 'New Password', value: next, onChange: setNext },
              { id: 'confirm', label: 'Confirm New Password', value: confirm, onChange: setConfirm },
            ] as const).map(({ id, label, value, onChange }) => (
              <div key={id} className="space-y-1.5">
                <label htmlFor={id} className="text-sm font-medium">{label}</label>
                <input
                  id={id}
                  type="password"
                  value={value}
                  onChange={(e) => (onChange as (v: string) => void)(e.target.value)}
                  required
                  minLength={id === 'current' ? 1 : 8}
                  className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                />
              </div>
            ))}
            <button
              type="submit"
              disabled={isPending}
              className="rounded-md bg-primary px-6 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {isPending ? 'Updating...' : 'Update Password'}
            </button>
          </form>
        </div>
      ) : (
        <div className="rounded-lg border border-border/50 bg-muted/30 p-4 space-y-2">
          <h2 className="text-sm font-semibold">SSO Authentication</h2>
          <p className="text-xs text-muted-foreground">
            Your account is managed by your organization's SSO provider. Password changes
            must be performed through your identity provider.
          </p>
        </div>
      )}
    </div>
  )
}
